import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import HistoryIcon from "@mui/icons-material/History";
import RvHookupIcon from "@mui/icons-material/RvHookup";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import TableRowsIcon from "@mui/icons-material/TableRows";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AuditLog, getAuditDisplayText, useGetAuditLogsQuery } from "../../../api/auditApi";
import { Column, DataTable } from "../../../components/common/DataTable/DataTable";
import {
  AuditEntitySummaryCard,
  DriverHistoryTab,
  getDriverHistoryTabForAudit,
} from "../auditEntityRegistry";
import AuditActionChip from "./AuditActionChip";
import DriverAssignmentTimeline from "./DriverAssignmentTimeline";

interface AuditHistoryDialogProps {
  open: boolean;
  entityType: string;
  entityId?: number | null;
  isResolvingEntity?: boolean;
  title?: string;
  unavailableMessage?: string;
  onClose: () => void;
}

const formatDateTime = (value: string) => new Date(value).toLocaleString();
const HISTORY_PAGE_SIZE = 25;

type DriverHistoryTabValue = "all" | DriverHistoryTab;

const DATATABLE_TABS: { value: DriverHistoryTabValue; label: string; icon: React.ReactElement }[] = [
  { value: "all", label: "All", icon: <HistoryIcon fontSize="small" /> },
  { value: "vehicle", label: "Vehicles", icon: <LocalShippingIcon fontSize="small" /> },
  { value: "trailer", label: "Trailers", icon: <RvHookupIcon fontSize="small" /> },
  { value: "dispatcher", label: "Dispatchers", icon: <SupportAgentIcon fontSize="small" /> },
];

const TIMELINE_TOGGLES: { value: DriverHistoryTab; label: string; icon: React.ReactElement }[] = [
  { value: "vehicle", label: "Vehicles", icon: <LocalShippingIcon fontSize="small" /> },
  { value: "trailer", label: "Trailers", icon: <RvHookupIcon fontSize="small" /> },
  { value: "dispatcher", label: "Dispatchers", icon: <SupportAgentIcon fontSize="small" /> },
];

const ALL_TIMELINE_TABS: DriverHistoryTab[] = ["vehicle", "trailer", "dispatcher"];

const getDateGroupKey = (auditLog: AuditLog) => {
  const createdAt = new Date(auditLog.createdAt);
  const dateKey = [
    createdAt.getFullYear(),
    String(createdAt.getMonth() + 1).padStart(2, "0"),
    String(createdAt.getDate()).padStart(2, "0"),
  ].join("-");
  const dateLabel = createdAt.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return `${dateKey}|${dateLabel}`;
};

const getDateGroupLabel = (groupKey: string) => groupKey.split("|")[1] ?? groupKey;

const AuditHistoryDialog: React.FC<AuditHistoryDialogProps> = ({
  open,
  entityType,
  entityId,
  isResolvingEntity = false,
  title,
  unavailableMessage,
  onClose,
}) => {
  const [page, setPage] = useState(0);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [activeDataTableTab, setActiveDataTableTab] = useState<DriverHistoryTabValue>("all");
  const [visibleTimelineTabs, setVisibleTimelineTabs] = useState<DriverHistoryTab[]>(ALL_TIMELINE_TABS);
  const [showDataTable, setShowDataTable] = useState(false);
  const hasEntityId = Boolean(entityId && entityId > 0);
  const resolvedEntityId = entityId ?? 0;
  const isDriverHistory = entityType === "EmployeeDriverInfo";

  const { data, isFetching } = useGetAuditLogsQuery(
    {
      entityType,
      entityId: resolvedEntityId,
      includeRelated: true,
      page: page + 1,
      pageSize: HISTORY_PAGE_SIZE,
    },
    { skip: !open || !hasEntityId }
  );

  useEffect(() => {
    setPage(0);
    setAuditLogs([]);
    setActiveDataTableTab("all");
    setVisibleTimelineTabs(ALL_TIMELINE_TABS);
    setShowDataTable(false);
  }, [entityId, entityType, open]);

  useEffect(() => {
    if (!open || !data) {
      return;
    }

    setAuditLogs((currentLogs) => {
      if (page === 0) {
        return data.data;
      }

      const existingIds = new Set(currentLogs.map((auditLog) => auditLog.id));
      const nextPageLogs = data.data.filter((auditLog) => !existingIds.has(auditLog.id));

      return [...currentLogs, ...nextPageLogs];
    });
  }, [data, open, page]);

  const handleLoadMore = useCallback(() => {
    if (data?.hasNext && !isFetching) {
      setPage((currentPage) => currentPage + 1);
    }
  }, [data?.hasNext, isFetching]);

  const displayedAuditLogs = useMemo(() => {
    if (!isDriverHistory) {
      return auditLogs;
    }

    const filteredAuditLogs =
      activeDataTableTab === "all"
        ? auditLogs
        : auditLogs.filter((auditLog) => getDriverHistoryTabForAudit(auditLog.entityType) === activeDataTableTab);

    return [...filteredAuditLogs].sort(
      (left, right) =>
        new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
    );
  }, [activeDataTableTab, auditLogs, isDriverHistory]);

  const auditColumns = useMemo<Column[]>(
    () => [
      {
        field: "createdAt",
        headerName: "Date",
        width: 170,
        renderCell: (value: string) => formatDateTime(value),
      },
      {
        field: "action",
        headerName: "Action",
        width: 190,
        renderCell: (value: string) => <AuditActionChip action={value} />,
      },
      {
        field: "actorName",
        headerName: "Actor",
        width: 180,
        renderCell: (_: string | null, item: AuditLog) =>
          item.actorName ||
          item.actorEmail ||
          (item.actorUserId ? `User #${item.actorUserId}` : "System"),
      },
      {
        field: "details",
        headerName: "Details",
        flex: 1,
        minWidth: 260,
        renderCell: (_: string | null, item: AuditLog) => getAuditDisplayText(item),
      },
    ],
    []
  );

  const renderAssetSummary = () => {
    if (!hasEntityId) {
      return isResolvingEntity ? (
        <Box sx={{ alignItems: "center", display: "flex", gap: 1, px: 1.25, py: 0.75 }}>
          <CircularProgress size={18} />
          <Typography color="text.secondary" sx={{ fontSize: "0.75rem" }}>
            Loading...
          </Typography>
        </Box>
      ) : (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          {unavailableMessage ?? "History details are not available for this entity."}
        </Alert>
      );
    }

    return (
      <AuditEntitySummaryCard
        entityType={entityType}
        entityId={resolvedEntityId}
        open={open}
        variant="overlay"
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          height: "95vh",
          maxHeight: "95vh",
          maxWidth: "95vw",
          width: "95vw",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.06)"
              : "background.paper",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
          px: 2,
          py: 1.25,
        }}
      >
        <Typography
          variant="h6"
          sx={(theme) => ({
            color: theme.palette.mode === "dark" ? "#fff" : "text.primary",
            fontSize: "1rem",
            fontWeight: 700,
            letterSpacing: 0,
          })}
        >
          {title ?? `History for ${entityType} #${resolvedEntityId}`}
        </Typography>
        <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
          {isDriverHistory && (
            <Button
              aria-pressed={showDataTable}
              onClick={() => setShowDataTable((current) => !current)}
              size="small"
              startIcon={<TableRowsIcon />}
              variant={showDataTable ? "contained" : "outlined"}
            >
              {showDataTable ? "Hide audit log" : "Show audit log"}
            </Button>
          )}
          <IconButton
            aria-label="Close audit history"
            onClick={onClose}
            sx={(theme) => ({
              color: theme.palette.mode === "dark" ? "#fff" : "text.secondary",
              opacity: 0.85,
              "&:hover": { opacity: 1 },
            })}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ display: "flex", flex: 1, flexDirection: "column", overflow: "hidden", p: 2 }}>
        <Box sx={{ display: "flex", flex: 1, flexDirection: "column", gap: 2, minHeight: 0 }}>
          {/* Timeline — 65% */}
          {isDriverHistory && (
            <Box sx={{ display: "flex", flex: showDataTable ? 65 : 1, flexDirection: "column", minHeight: 0 }}>
              <DriverAssignmentTimeline
                auditLogs={auditLogs}
                visibleTabs={visibleTimelineTabs}
                isLoading={isResolvingEntity || (isFetching && page === 0 && auditLogs.length === 0)}
                open={open}
                topLeftOverlay={renderAssetSummary()}
                topRightOverlay={
                  <Stack direction="row" spacing={0.75}>
                    {TIMELINE_TOGGLES.map((toggle) => {
                      const active = visibleTimelineTabs.includes(toggle.value);
                      return (
                        <Box
                          key={toggle.value}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            setVisibleTimelineTabs((prev) =>
                              active
                                ? prev.filter((t) => t !== toggle.value)
                                : [...prev, toggle.value]
                            );
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setVisibleTimelineTabs((prev) =>
                                active
                                  ? prev.filter((t) => t !== toggle.value)
                                  : [...prev, toggle.value]
                              );
                            }
                          }}
                          aria-label={toggle.label}
                          aria-pressed={active}
                          sx={{
                            alignItems: "center",
                            bgcolor: active ? "primary.main" : "background.paper",
                            borderRadius: 5,
                            boxShadow: 1,
                            color: active ? "primary.contrastText" : "text.secondary",
                            cursor: "pointer",
                            display: "flex",
                            gap: 0.5,
                            px: 1.25,
                            py: 0.5,
                            transition: "all 0.15s ease",
                            userSelect: "none",
                            "&:hover": {
                              bgcolor: active ? "primary.dark" : "action.hover",
                              boxShadow: 2,
                            },
                          }}
                        >
                          {toggle.icon}
                          <Typography
                            sx={{
                              color: "inherit",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                            }}
                          >
                            {toggle.label}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                }
              />
            </Box>
          )}

          {(!isDriverHistory || showDataTable) && (
            <Card variant="outlined" sx={{ display: "flex", flex: isDriverHistory ? 35 : 1, flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
            {isDriverHistory && (
              <Box sx={{ borderBottom: 1, borderColor: "divider", px: 1 }}>
                <Tabs
                  value={activeDataTableTab}
                  onChange={(_event, nextValue: DriverHistoryTabValue) =>
                    setActiveDataTableTab(nextValue)
                  }
                  variant="scrollable"
                  scrollButtons="auto"
                  aria-label="audit history data table filters"
                >
                  {DATATABLE_TABS.map((tab) => (
                    <Tab
                      key={tab.value}
                      value={tab.value}
                      icon={tab.icon}
                      iconPosition="start"
                      label={tab.label}
                      sx={{ minHeight: 40, py: 0.5, fontSize: "0.8rem" }}
                    />
                  ))}
                </Tabs>
              </Box>
            )}
            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 0 }}>
              <DataTable<AuditLog>
                items={displayedAuditLogs}
                columns={auditColumns}
                title="Audit History"
                isLoading={isResolvingEntity || (isFetching && page === 0 && auditLogs.length === 0)}
                isLoadingRows={isResolvingEntity || (isFetching && page === 0 && auditLogs.length === 0)}
                isLoadingMore={isFetching && page > 0}
                totalCount={data?.totalRecords ?? auditLogs.length}
                hasNextPage={Boolean(data?.hasNext)}
                onLoadMore={handleLoadMore}
                getRowId={(auditLog) => auditLog.id}
                fitContentHeight
                grouping={
                  isDriverHistory && activeDataTableTab === "all"
                    ? {
                        getGroupKey: getDateGroupKey,
                        renderGroupHeader: (groupKey, groupItems) => (
                          <Box
                            sx={{
                              alignItems: "center",
                              color: "text.primary",
                              display: "flex",
                              gap: 1,
                            }}
                          >
                            <CalendarMonthIcon color="inherit" fontSize="small" />
                            <Typography variant="subtitle2" color="text.primary" sx={{ fontWeight: 700 }}>
                              {getDateGroupLabel(groupKey)}
                            </Typography>
                            <Chip size="small" label={groupItems.length} />
                          </Box>
                        ),
                        defaultExpanded: true,
                        allowFullCollapse: true,
                        collapsedItemLimit: 0,
                        itemLabel: "audit",
                        countMode: "items",
                      }
                    : undefined
                }
              />
            </Box>
          </Card>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AuditHistoryDialog;
